import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Package, ShoppingBag, Phone, Truck, Banknote } from 'lucide-react';

const SUPPORT_PHONE_DISPLAY = '+880 1618-871511';
const SUPPORT_PHONE_TEL = 'tel:+8801618871511';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const order = location.state?.order;

  // This page is only meaningful right after a successful checkout — the order
  // snapshot travels via router state and isn't persisted, so a reload or a
  // direct link here has nothing to show.
  useEffect(() => {
    if (!order) navigate('/', { replace: true });
  }, [order, navigate]);

  if (!order) return null;

  const {
    orderNumber, name, phone, address, district, area,
    items, subtotal, discountAmount, promoCode, courierCost, isFreeShipping, total,
  } = order;

  const addressLine2 = [area, district].filter(Boolean).join(', ');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to shop
      </Link>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left: confirmation details */}
        <div className="flex-1 space-y-6">
          <div className="flex items-start gap-3.5">
            <div className="w-11 h-11 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs font-mono text-gray-500 mb-0.5">Confirmation #{orderNumber}</p>
              <h1 className="text-2xl font-bold text-gray-900">Thank you, {name}!</h1>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2.5">Your order is confirmed</h2>
            <p className="text-sm font-semibold text-gray-700 leading-relaxed mb-2.5">
              Your order has been placed successfully! To ensure smooth delivery, we&apos;ll call you within 24 hours to confirm the details.
            </p>
            <p className="text-sm text-gray-700 leading-[1.85]">
              আপনার অর্ডারটি সফলভাবে সম্পন্ন হয়েছে! ডেলিভারি নিশ্চিত করতে আমরা আগামী ২৪ ঘণ্টার মধ্যে একটি নিশ্চিতকরণ কলের মাধ্যমে আপনার সাথে যোগাযোগ করব।
            </p>
            <hr className="border-gray-100 my-4" />
            <div className="flex items-start gap-2.5 bg-primary-50 rounded-xl px-3.5 py-3">
              <Phone className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-gray-700 leading-relaxed">
                Please keep <span className="font-semibold text-gray-900">{phone}</span> reachable — our sales executive will call from this number.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Delivery address</h3>
                <p className="text-sm text-gray-900">{name}</p>
                <p className="text-sm text-gray-900">{address}</p>
                {addressLine2 && <p className="text-sm text-gray-900">{addressLine2}</p>}
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full mt-2">
                  <Truck className="w-3.5 h-3.5" />
                  Home Delivery{district ? ` — ${district}` : ''}
                </span>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Contact &amp; payment</h3>
                <p className="text-sm text-gray-900">{phone}</p>
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full mt-2">
                  <Banknote className="w-3.5 h-3.5" />
                  Cash on Delivery
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Need help?{' '}
              <a href={SUPPORT_PHONE_TEL} className="text-primary-600 font-semibold hover:text-primary-700">
                Contact us ({SUPPORT_PHONE_DISPLAY})
              </a>
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors text-sm"
            >
              <ShoppingBag className="w-4 h-4" />
              Continue Shopping
            </Link>
          </div>
        </div>

        {/* Right: order summary */}
        <div className="lg:w-80">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 lg:sticky lg:top-24">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Order Summary</h2>

            <div className="space-y-3 mb-4">
              {items.map((item) => (
                <div key={`${item.id}-${item.size}`} className="flex items-start gap-3">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">Size: {item.size} · Qty: {item.quantity}</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">
                      ৳{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-3 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>৳{subtotal.toLocaleString()}</span>
              </div>
              {discountAmount > 0 && !isFreeShipping && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount{promoCode ? ` (${promoCode})` : ''}</span>
                  <span>- ৳{discountAmount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery charge</span>
                {isFreeShipping ? (
                  <span className="font-medium">
                    <span className="line-through text-gray-400 mr-1.5">৳{courierCost.toLocaleString()}</span>
                    <span className="text-green-600 font-semibold">FREE</span>
                  </span>
                ) : (
                  <span className="font-medium text-gray-700">৳{courierCost.toLocaleString()}</span>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between font-semibold text-gray-900">
              <span>Total</span>
              <span>৳{total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
